import { Box, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { OLButton } from "OLButton";
import { OLPaper } from "OLPaper";
import OverflowChipTooltip from "components/Tooltips/OverflowChipTooltip";
import { TooltipWithIcon } from "components/Tooltips/TooltipWithIcon";
import { useMessage } from "hooks/intl";
import { useEffect, useMemo, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { TransactionBackendModel } from "types/transactions";
import { OLDate } from "utilities/dateFormat";
import { formatCurrencyValue } from "utilities/numbers";
import { KeyValuePair } from "utilities/objects";
import { useStyles } from "./SingleTransaction.styles";

interface TransactionDetailType extends KeyValuePair {
  tooltip?: React.ReactElement;
  "data-test-id": string;
}

interface Props {
  transactionData?: TransactionBackendModel;
  openCustomAttributesModal: () => void;
}

export function TransactionDetailsSection({
  transactionData,
  openCustomAttributesModal,
}: Props) {
  const classes = useStyles();
  const { message } = useMessage();
  const [chipsContainerCurrentHeight, setChipsContainerCurrentHeight] =
    useState(0);
  const refElement = useRef<HTMLDivElement>(null);

  const chipsContainerMaxHeight = 112;
  const isShowAllBtnVisible =
    chipsContainerCurrentHeight > chipsContainerMaxHeight;
  const isLoading = !transactionData;
  const transactionDataLabels = transactionData?.header?.labels;

  const fields: TransactionDetailType[] = useMemo(
    () =>
      [
        {
          key: message({ id: "documentNumber" }),
          value: transactionData?.header?.documentNumber,
          "data-test-id": "transaction-details-document-number",
        },
        {
          key: message({ id: "documentType" }),
          value: message({
            id:
              transactionData?.header?.documentType === "sell"
                ? "purchase"
                : "return",
          }),
          "data-test-id": "transaction-details-document-type",
        },
        {
          key: message({ id: "documentDate" }),
          value: OLDate(transactionData?.header?.purchasedAt),
          "data-test-id": "transaction-details-document-date",
        },
        {
          key: message({ id: "channel" }),
          value: transactionData?.channelName,
          tooltip: (
            <>
              <Typography className={classes.tooltipTitle}>
                {message({ id: "channel" })}
              </Typography>
              <Typography className={classes.tooltipDescription}>
                {message({ id: "channelInfo" })}
              </Typography>
            </>
          ),
          "data-test-id": "transaction-details-document-channel",
        },
        {
          key: message({ id: "purchasePlace" }),
          value: transactionData?.header?.purchasePlace,
          tooltip: (
            <>
              <Typography className={classes.tooltipTitle}>
                {message({ id: "purchasePlace" })}
              </Typography>
              <Typography className={classes.tooltipDescription}>
                {message({ id: "purchasePlaceInfo" })}
              </Typography>
            </>
          ),
          "data-test-id": "transaction-details-purchase-place",
        },
        {
          key: message(
            { id: "transactionValueWithCurrency" },
            { currency: transactionData?.currency },
          ),
          value: formatCurrencyValue(transactionData?.grossValue),
          tooltip: (
            <>
              <Typography className={classes.tooltipTitle}>
                {message({ id: "transactionValue" })}
              </Typography>
              <Typography className={classes.tooltipDescription}>
                {message({ id: "transactionValueInfo" })}
              </Typography>
            </>
          ),
          "data-test-id": "transaction-details-value",
        },
        {
          key: message({ id: "status" }),
          value: transactionData?.matched
            ? message({ id: "matched" })
            : message({ id: "notMatched" }),
          tooltip: (
            <>
              <Typography className={classes.tooltipTitle}>
                {message({ id: "status" })}
              </Typography>
              <Typography className={classes.tooltipDescription}>
                {message({ id: "statusInfo" })}
              </Typography>
            </>
          ),
          "data-test-id": "transaction-details-status",
        },
        {
          key: message({ id: "transactionSystemId" }),
          value: transactionData?.transactionId,
          "data-test-id": "transaction-details-value",
        },
      ] as TransactionDetailType[],
    [
      transactionData,
      message,
      classes.tooltipDescription,
      classes.tooltipTitle,
    ],
  );

  useEffect(() => {
    if (!refElement.current) return;
    const resizeObserver = new ResizeObserver(() => {
      setChipsContainerCurrentHeight(
        refElement.current?.clientHeight as number,
      );
    });
    resizeObserver.observe(refElement.current);
    return () => resizeObserver.disconnect();
  }, []);

  return (
    <Grid item xs={7} display="flex" flexDirection="column">
      <Typography className={classes.sectionTitle}>
        {message({ id: "transactionDetails" })}
      </Typography>
      <OLPaper className={`${classes.details} ${classes.detailsSection}`}>
        <Grid item container className={classes.gridContainer} rowSpacing={8}>
          {fields.map(
            (
              { key, value, tooltip, "data-test-id": dataTestId },
              index: number,
            ) => (
              <Grid item xs={12} key={index} className={classes.detailsItem}>
                <Box className={classes.textWithTooltipBox}>
                  <Typography
                    className={classes.smallText}
                    data-test-id={`${dataTestId}-key`}
                  >
                    {key}
                  </Typography>
                  {tooltip && (
                    <TooltipWithIcon
                      className={classes.tooltip}
                      isWhite
                      text={tooltip}
                      data-test-id={`${dataTestId}-tooltip`}
                    />
                  )}
                </Box>
                <Typography
                  className={classes.largeText}
                  data-test-id={`${dataTestId}-value`}
                >
                  {isLoading ? <Skeleton width={100} /> : value || "-"}
                </Typography>
              </Grid>
            ),
          )}
          <Grid item xs={12} className={classes.detailsItem}>
            <Typography
              className={classes.smallText}
              data-test-id="transaction-details-custom-attribute-key"
            >
              {message({ id: "customAttributes" })}
            </Typography>
            <Grid
              item
              className={`${classes.chipsContainer} 
            ${
              chipsContainerCurrentHeight >= chipsContainerMaxHeight
                ? classes.hidden
                : classes.visible
            }
            `}
              mt={4}
              maxHeight={113}
              ref={refElement}
            >
              {isLoading ? (
                <Skeleton width={100} />
              ) : (
                (transactionDataLabels?.length &&
                  transactionDataLabels.map(
                    (label: KeyValuePair, index: number) => (
                      <OverflowChipTooltip
                        key={index}
                        placement="top"
                        label={`${label.key}: ${label.value}`}
                        data-test-id={`transaction-details-${index}`}
                      />
                    ),
                  )) ||
                "-"
              )}
            </Grid>
            {isShowAllBtnVisible && (
              <Grid item mt={4}>
                <OLButton
                  className={classes.showMoreBtn}
                  onClick={openCustomAttributesModal}
                  data-test-id="transaction-details-show-all-button"
                >
                  {message(
                    { id: "showAllElements" },
                    { elements: transactionDataLabels?.length },
                  )}
                </OLButton>
              </Grid>
            )}
          </Grid>
        </Grid>
      </OLPaper>
    </Grid>
  );
}
